import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Tag, message, Empty, Spin, Button, Popconfirm, Tooltip } from 'antd';
import { UndoOutlined, EyeOutlined } from '@ant-design/icons';
import {
  getRejections,
  undoRejectOpportunity,
  getOpportunityLocation,
} from '../../../services/api';
import { RejectionWithOpportunity, LLMRating, RejectionReason } from '../../../types';
import { getApiErrorMessage } from '../../../utils/errorUtils';
import RejectedOpportunityDrawer from '../../../components/drawers/RejectedOpportunityDrawer';
import { useListPageState } from '../../../hooks/useListPageState';
import css from './RejectedTab.module.scss';

interface RejectedTabProps {
  searchId?: string;
  refreshKey?: number;
  onUndo?: () => void;
}

const ratingConfig: Record<LLMRating, { stars: string; color: string; label: string }> = {
  perfect_fit: { stars: '★★★★★', color: '#52c41a', label: 'Perfect Fit' },
  strong_alignment: { stars: '★★★★☆', color: '#73d13d', label: 'Strong' },
  potential_candidate: { stars: '★★★☆☆', color: '#faad14', label: 'Potential' },
  partial_fit: { stars: '★★☆☆☆', color: '#fa8c16', label: 'Partial' },
  misalignment: { stars: '★☆☆☆☆', color: '#ff4d4f', label: 'Weak' },
};

const rejectionReasonConfig: Record<RejectionReason, { label: string; color: string }> = {
  not_interested: { label: 'Not Interested', color: 'default' },
  bad_fit: { label: 'Bad Fit', color: 'red' },
  company_concerns: { label: 'Company Concerns', color: 'orange' },
  salary_expectations: { label: 'Salary', color: 'gold' },
  location_mismatch: { label: 'Location', color: 'blue' },
  other: { label: 'Other', color: 'purple' },
};

const UndoAction: React.FC<{ onConfirm: () => void }> = ({ onConfirm }) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);

  return (
    <Popconfirm
      title="Undo this rejection?"
      onConfirm={onConfirm}
      onOpenChange={(open) => { if (open) setTooltipOpen(false); }}
      okText="Yes"
      cancelText="No"
      overlayClassName={css.undoPopconfirm}
    >
      <Tooltip title="Undo rejection" open={tooltipOpen} onOpenChange={setTooltipOpen}>
        <Button
          type="default"
          icon={<UndoOutlined />}
          className={css.undoButton}
          size="small"
          onClick={() => setTooltipOpen(false)}
        />
      </Tooltip>
    </Popconfirm>
  );
};

const RejectedTab: React.FC<RejectedTabProps> = ({ searchId, refreshKey, onUndo }) => {
  const [rejections, setRejections] = useState<RejectionWithOpportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const { selectedId, page, pageSize, setPage, openDrawer, closeDrawer, redirectToBucket } =
    useListPageState({ basePath: '/opportunities/rejected' });
  const navigate = useNavigate();

  const selectedRejection = useMemo<RejectionWithOpportunity | null>(
    () => (selectedId ? rejections.find(r => r.opportunity.id === selectedId) ?? null : null),
    [rejections, selectedId]
  );
  const drawerOpen = Boolean(selectedRejection);

  const fetchRejections = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getRejections({
        skip: (page - 1) * pageSize,
        limit: pageSize,
        searchId,
        sort_field: 'rejected_at',
        sort_method: 'desc',
      });
      setRejections(response.rejections);
      setTotal(response.total);
    } catch (error) {
      console.error('Fetch rejections error:', error);
      message.error(getApiErrorMessage(error));
    } finally {
      setLoading(false);
    }
    // `refreshKey` is listed but never read here, and that is the point: it is
    // the parent's refetch channel — Opportunities.tsx bumps it when a reject
    // lands in the sibling tab — and this callback's identity is how that
    // reaches the effect below. Excluding it, which is what the rule asks for,
    // removes the channel outright; nothing else in this list moves with it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, searchId, refreshKey]);

  useEffect(() => {
    fetchRejections();
  }, [fetchRejections]);

  useEffect(() => {
    if (page !== 1) setPage(1, undefined, { replace: true });
  }, [searchId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve deep links: locate the id and redirect to regular tab if needed.
  useEffect(() => {
    if (!selectedId) return;
    if (loading) return;
    if (rejections.some(r => r.opportunity.id === selectedId)) return;

    let cancelled = false;
    (async () => {
      try {
        const loc = await getOpportunityLocation(selectedId, pageSize);
        if (cancelled) return;
        if (loc.bucket === 'regular') {
          redirectToBucket('/opportunities', selectedId, loc.page);
          return;
        }
        if (loc.page !== page) {
          setPage(loc.page, undefined, { replace: true });
        }
      } catch (error) {
        if (cancelled) return;
        message.error('Rejected opportunity not found');
        navigate('/opportunities/rejected', { replace: true });
      }
    })();
    return () => { cancelled = true; };
    // The pagination handles are read but not listed, on purpose. `setPage`,
    // `redirectToBucket` and `navigate` are all rebuilt whenever the URL
    // changes — useListPageState keys them on `searchParams` — and changing the
    // URL is exactly what this effect does. Listing them, or `page`/`pageSize`,
    // re-enters the effect from its own `setPage` before the refetch it
    // triggers has landed, firing a second getOpportunityLocation for an id
    // that is already being located. `loading`
    // and the list are the honest triggers: they are what tells us whether the
    // id is on screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, rejections, loading]);

  const handleUndo = async (opportunityId: string) => {
    try {
      await undoRejectOpportunity(opportunityId);
      message.success('Rejection undone');
      if (selectedId === opportunityId) closeDrawer();
      fetchRejections();
      onUndo?.();
    } catch (error) {
      message.error(getApiErrorMessage(error));
    }
  };

  const handleViewDetails = (record: RejectionWithOpportunity) => {
    openDrawer(record.opportunity.id);
  };

  const handleDrawerClose = () => {
    closeDrawer();
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const columns = [
    {
      title: 'Rating',
      key: 'rating',
      width: 120,
      render: (_: unknown, record: RejectionWithOpportunity) => {
        const config = ratingConfig[record.opportunity.llmRating];
        return (
          <span style={{ color: config.color, letterSpacing: '2px' }} title={config.label}>
            {config.stars}
          </span>
        );
      },
    },
    {
      title: 'Title',
      key: 'title',
      width: 420,
      render: (_: unknown, record: RejectionWithOpportunity) => (
        // TODO(a11y): make this a real <button type="button">, the way
        // OpportunitiesTab's title cell already is. Deferred rather than done
        // here because the swap is not a one-liner: this file's `.titleLink`
        // styles an <a>, so it carries none of the UA-button reset
        // (appearance/background/border/padding/font/text-align/white-space/
        // max-width) that OpportunitiesTab.module.scss needed, and changing the
        // element without that CSS would visibly alter the desktop table —
        // which CLAUDE.md pins as the frozen reference. Until then the row's
        // "View" Button calls the same handleViewDetails and is
        // keyboard-reachable; unlike OpportunitiesTab this table keeps that
        // column at every viewport, so nothing is stranded behind a pointer.
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        <a onClick={() => handleViewDetails(record)} className={css.titleLink}>
          {record.opportunity.title}
        </a>
      ),
    },
    {
      title: 'Company',
      dataIndex: ['company', 'name'],
      key: 'company',
      width: 250,
    },
    {
      title: 'Search',
      dataIndex: ['opportunity', 'search', 'searchRole'],
      key: 'search',
      width: 200,
    },
    {
      title: 'Location',
      dataIndex: ['opportunity', 'search', 'searchLocation'],
      key: 'location',
      width: 240,
    },
    {
      title: 'Rejection Reason',
      key: 'rejectionReason',
      render: (_: unknown, record: RejectionWithOpportunity) => {
        const config = rejectionReasonConfig[record.rejectionReason];
        const displayLabel = record.rejectionReason === 'other' && record.rejectionReasonText
          ? record.rejectionReasonText
          : config.label;
        return (
          <Tag color={config.color}>
            {displayLabel}
          </Tag>
        );
      },
    },
    {
      title: 'Rejected Date',
      key: 'rejectedDate',
      width: 130,
      render: (_: unknown, record: RejectionWithOpportunity) => (
        <span>{formatDate(record.rejectedAt)}</span>
      ),
    },
    {
      title: '',
      key: 'view',
      width: 90,
      render: (_: unknown, record: RejectionWithOpportunity) => (
        <Button
          type="text"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetails(record)}
        >
          View
        </Button>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      align: 'center' as const,
      render: (_: unknown, record: RejectionWithOpportunity) => (
        <UndoAction
          onConfirm={() => handleUndo(record.opportunity.id)}
        />
      ),
    },
  ];

  if (loading && rejections.length === 0) {
    return (
      <div className={css.loadingContainer}>
        <Spin size="large" />
      </div>
    );
  }

  if (!loading && rejections.length === 0) {
    return (
      <div className={css.emptyContainer}>
        <Empty
          description="No rejected opportunities"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          <p>Opportunities you reject will appear here for reference.</p>
        </Empty>
      </div>
    );
  }

  return (
    <div className={css.rejectedTab}>
      <Table
        dataSource={rejections}
        columns={columns}
        rowKey="id"
        loading={loading}
        onChange={(pagination) => {
          setPage(pagination.current ?? 1, pagination.pageSize ?? 10);
        }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (total) => `${total} rejections`,
        }}
      />

      <RejectedOpportunityDrawer
        rejection={selectedRejection}
        open={drawerOpen}
        onClose={handleDrawerClose}
      />
    </div>
  );
};

export default RejectedTab;
